/* eslint-disable no-unused-vars */
import { DocHubDocument } from "@/scripts/js/forgeModels";
import store from "../store/index";
import HttpService from '@/utils/httpService';
import { $handleError } from '@/utils';
import config from "../../appConfig";
import { objectToQueryParams } from "@/scripts/js/forgeService";
import { log as debugLog } from '@/utils/debugLogger';
import { resolveDocumentNameConflicts, reportVersionUploads } from '@/features/Documents/utils/nameConflictResolver';
import { isNewVersionDraft } from '@/features/Documents/utils/documentNameConflicts';

/**
 * @deprecated SAS tokens are now handled server-side. The backend returns pre-signed URLs.
 * These functions are kept for backward compatibility but should be removed in future versions.
 * SECURITY: Client-side token caching has been removed to prevent token exposure.
 */
export function checkExpires() {
    // SECURITY FIX: No longer caching tokens client-side
    // Backend now returns pre-signed URLs with each response
    return Promise.resolve();
}

/**
 * @deprecated SAS tokens are now handled server-side.
 */
export function findSasToken() {
    // SECURITY FIX: No longer caching tokens client-side
    return Promise.resolve('');
}

/**
 * @deprecated SAS tokens are now handled server-side.
 */
export function getSasToken() {
    // SECURITY FIX: No longer fetching tokens for client-side caching
    // Backend returns pre-signed URLs directly
    return Promise.resolve('');
}

/**
 * Upload files to a folder with optional scope settings for cross-facility support.
 * @param {File[]} files - Files to upload
 * @param {string} folder - Target folder name
 * @param {string|null} documentTypeId - Document type ID (optional)
 * @param {Object} scopeOptions - Scope options for cross-facility documents
 * @param {'Facility' | 'FacilityGroup'} scopeOptions.scopeType - Scope type
 * @param {string|null} scopeOptions.facilityGroupId - Facility group ID (when scopeType is 'FacilityGroup')
 * @param {Object} [options] - For uploads that run in the background (see DOCUMENT_UPLOADS store module)
 * @param {string} [options.assetId] - Facility captured when the upload was queued; defaults to the current one
 * @param {Object|null} [options.predefined] - Metadata snapshot captured when queued; defaults to GET_PREDEFINED_DOCUMENT_INFO
 * @param {AbortSignal} [options.signal] - Aborts the file transfer; the request is then never auto-cancelled on navigation
 * @param {Function} [options.onPhase] - (phase, detail) for 'checking' | 'uploading' | 'saving'; 'uploading' carries { fileIndexes }
 * @param {Function} [options.onProgress] - (loadedBytes, totalBytes) while files are transferred
 * @param {boolean} [options.openReview=true] - Open the Update Documents popup for the created documents
 * @param {boolean} [options.throwOnError=false] - Reject on failure instead of showing the error and resolving []
 * @returns {Promise<DocHubDocument[]>} Created documents
 */
export async function uploadFilesToFolder(files, folder, documentTypeId = null, scopeOptions = null, options = {}) {
    const assetId = options.assetId || store.state.assetId;
    const requestOptions = { signal: options.signal, throwOnError: !!options.throwOnError };

    // Check if there are files to upload
    if (!files || files.length === 0) {
        return [];
    }

    // Check if there is a document type
    if (!documentTypeId) {
        documentTypeId = store.getters['GET_DEFAULT_DOCUMENT_TYPE_ID'];
    }

    // Get scope settings from options or store
    const scopeType = scopeOptions?.scopeType || store.getters['GET_UPLOAD_SCOPE_TYPE'] || 'Facility';
    const facilityGroupId = scopeOptions?.facilityGroupId || store.getters['GET_UPLOAD_FACILITY_GROUP_ID'] || null;
    const isGroupScoped = scopeType === 'FacilityGroup' && !!facilityGroupId;

    // Names taken in the facility (or group): ask per file for a new version, another name, or skip,
    // before anything is uploaded.
    options.onPhase?.('checking');
    const selected = files;
    const drafts = await resolveDocumentNameConflicts(
        files.map((file, index) => {
            const stem = file.name.split('.').slice(0, -1).join('.');
            return { documentNumber: stem, documentTitle: stem, fileName: file.name, fileIndex: index };
        }),
        isGroupScoped ? { facilityGroupId } : { assetId },
        // Lets the prompt flag a file identical to the current version of the document it would update.
        { fileOf: (draft) => selected[draft.fileIndex] },
    );
    if (!drafts || drafts.length === 0) {
        return [];
    }
    files = drafts.map(draft => files[draft.fileIndex]);

    // Upload files to the server
    options.onPhase?.('uploading', { fileIndexes: drafts.map(draft => draft.fileIndex) });
    let documentsRefs = await UPLOAD_DOCUMENTS(files, {
        ...requestOptions,
        onUploadProgress: options.onProgress
            ? (event) => options.onProgress(event.loaded, event.total)
            : undefined,
    });

    if (documentsRefs.length === 0) {
        return [];
    }

    let predefined = 'predefined' in options ? options.predefined : store.getters['GET_PREDEFINED_DOCUMENT_INFO'];

    if (predefined) {
        predefined = structuredClone(predefined);
    }

    // Create documents with appropriate scope
    let documents = documentsRefs.map((file, index) => {
        let document = Object.assign(new DocHubDocument, file);
        let fileNameWithoutExtension = file.fileName.split('.').slice(0, -1).join('.');
        document.documentTitle = fileNameWithoutExtension;
        document.documentNumber = fileNameWithoutExtension;
        document.folderName = folder;
        document.documentTypeId = documentTypeId;
        document.revisionNumber = "0";
        document.size = file.size;

        // Set scope based on scopeType (mutually exclusive)
        if (isGroupScoped) {
            document.assetId = null; // Group-scoped documents don't have a facility
            document.facilityGroupId = facilityGroupId;
        } else {
            document.assetId = assetId;
            document.facilityGroupId = null;
        }

        debugLog('attachmentsAPI', 'Predefined:', predefined);
        document = Object.assign(document, predefined);

        // The user's answer for a taken name: renamed, or added as a new version of the existing document.
        const draft = drafts[index];
        if (draft) {
            document.documentNumber = draft.documentNumber;
            document.documentTitle = draft.documentTitle;
            document.fileName = draft.fileName;
            document.onNameConflict = draft.onNameConflict;
            document.targetDocumentId = draft.targetDocumentId;
            // New version only: whether the links on the current version are copied onto it.
            document.transferLinks = draft.transferLinks === true;
            // New version only: the user chose to add it although it is identical to the current version.
            document.allowDuplicateContent = draft.allowDuplicateContent === true;
        }
        debugLog('attachmentsAPI', 'Document with scope:', {
            scopeType,
            assetId: document.assetId,
            facilityGroupId: document.facilityGroupId
        });

        return document;
    });

    // Save documents to the database. Not cancellable: once the files are stored, the documents
    // are created even if the user navigates away.
    options.onPhase?.('saving');
    const { documents: savedDocuments, headers } = await CREATE_DOCUMENTS_WITH_RESPONSE(documents, {
        throwOnError: requestOptions.throwOnError,
        critical: !!options.signal,
    });

    if (savedDocuments.length === 0) {
        return [];
    }

    // Say what happened to files sent as new versions (now vN / pinned version stays / identical).
    reportVersionUploads(documents, savedDocuments, headers);

    store.dispatch('FETCH_ALL_DOCUMENTS');

    // Only documents this upload created go to the metadata popup: its Delete removes them, and a
    // file added as a new version belongs to a document that existed before.
    const createdIds = savedDocuments.filter((d, i) => !isNewVersionDraft(documents[i])).map(a => a.id);
    if (createdIds.length > 0 && options.openReview !== false) {
        store.dispatch('UPDATE_UPLOADED_DOCUMENTS', { ids: createdIds });
    }

    return savedDocuments
}

export function GET_DOCUMENT_TYPES(config = {}) {
    return new Promise((resolve, reject) => {
        HttpService.get({
            ...config,
            url: `api/document-types`,
            params: {
                page: -1,
                itemsPerPage: -1,
                sortBy: 'Name',
                sortDesc: false,
                isForge: true
            }
        }).then((response) => {
            let types = response.data;
            resolve(types);
        }).catch(reject);
    })
}

/**
 * @param {File[]} files
 * @param {{ signal?: AbortSignal, onUploadProgress?: Function, throwOnError?: boolean }} [options]
 */
export async function UPLOAD_DOCUMENTS(files, { signal, onUploadProgress, throwOnError = false } = {}) {
    let formData = new FormData();

    files.forEach(file => {
        formData.append("files", file);
    });

    return HttpService.post({
        url: `api/documents/uploadFiles`,
        data: formData,
        signal,
        onUploadProgress,
    }).then((response) => {
        return response.data ?? [];
    }).catch((error) => {
        if (throwOnError) throw error;
        $handleError(error);
        return [];
    });
}

export function CREATE_DOCUMENT(document) {
    return new Promise((resolve, reject) => {
        HttpService.post({
            url: `api/documents`,
            data: document,
        }).then((response) => {
            resolve(response.data);
        }).catch(reject);
    })
}

export function CREATE_DOCUMENTS(documents) {
    return CREATE_DOCUMENTS_WITH_RESPONSE(documents).then(({ documents: saved }) => saved);
}

/**
 * POST AddDocuments, keeping the response headers (X-Document-Version-Duplicates). Resolves
 * `{ documents: [], headers: {} }` after showing the error when the request fails, or rejects
 * when `throwOnError` is set.
 */
export function CREATE_DOCUMENTS_WITH_RESPONSE(documents, { throwOnError = false, critical = false } = {}) {
    return HttpService.post({
        url: `api/documents/AddDocuments`,
        data: documents,
        critical,
    }).then((response) => ({
        documents: response.data ?? [],
        headers: response.headers ?? {},
    })).catch((error) => {
        if (throwOnError) throw error;
        $handleError(error);
        return { documents: [], headers: {} };
    });
}

export function DELETE_DOCUMENTS(ids) {
    return new Promise((resolve, reject) => {
        HttpService.delete({
            url: `api/documents/DeleteDocuments`,
            data: ids,
        }).then((response) => {
            store.dispatch('FETCH_ALL_DOCUMENTS');
            resolve(response.data);
        }).catch(reject);
    })
}

export function UPDATE_DOCUMENT(document) {
    // Don't send storageReference/url on updates - they only change with new file uploads
    // This prevents signed URLs from being saved back to the database
    const { storageReference, url, ...updateData } = document;

    return new Promise((resolve, reject) => {
        HttpService.put({
            url: `api/documents/${document.id}`,
            data: updateData,
        }).then((response) => {
            resolve(response.data);
        }).catch(reject);
    })
}

export function GET_DOCUMENTS_BY_QUERY(queryParameters) {
    return new Promise((resolve, reject) => {
        HttpService.get({
            url: `api/documents/TypeHead?${queryParameters}`,
        }).then((response) => {
            let { items } = response.data;
            resolve(items);
        }).catch(reject);
    })
}

export function GET_ALL_DOCUMENTS(query, config = {}) {
    return new Promise((resolve, reject) => {
        HttpService.get({
            url: `api/documents/TypeHead?${objectToQueryParams(query)}`,
            ...config,
        }).then((response) => {
            let { items, total } = response.data;
            // URLs are now pre-signed by the backend
            items.forEach(a => {
                a.originalLinkedForgeTagsIds = [...(a.forgeTagsIds ?? [])];
                a.url = decodeURI(`${a.storageReference}`);
            })
            resolve({items, total});
        }).catch(reject);
    })
}

export function GET_DOCUMENT_BY_ID(id) {
    return new Promise((resolve, reject) => {
        HttpService.get({
            url: `api/documents/${id}`,
        }).then((response) => {
            let document = response.data;
            // URLs are now pre-signed by the backend
            document.originalLinkedForgeTagsIds = [...(document.forgeTagsIds ?? [])];
            document.url = decodeURI(`${document.storageReference}`);
            resolve(document);
        }).catch(reject);
    })
}

export function deleteAttachmentByReferenceIds(ids) {
    return new Promise((resolve, reject) => {
        HttpService.post({
            url: `api/forge/attachments/deleteAttachmentByReferenceIds`,
            data: { ids },
        }).then(resolve).catch(reject);
    })
}

export function getEmptyFileUrl() {
    return `${config.API_URL}api/publicfiles/emptyPage.pdf`;
}